<template>
  <div v-if="activity" class="activity-card" @click="onClick(formattedActivity)">
    <div class="card-content">
      <div class="image-container">
        <img :src="formattedActivity.icon" :style="{ height: '3rem' }" />
      </div>
      <div>
        <h4 v-if="formattedActivity.donation">
          {{ $gettext('formattedActivity One time') }} {{ $gettext(formattedActivity.actionType) }}
        </h4>

        <h4 v-else>{{ $gettext(formattedActivity.actionType) }}</h4>
        <span v-if="formattedActivity.request">
          {{ $gettext('Status:') }} {{ $gettext(formattedActivity.request.statusDisplay) }}
          <br />
          {{ formattedActivity.request.amount | currency }}
        </span>
        <span v-else>
          {{ $gettext('Status:') }}
          {{
            formattedActivity.donation.status === 'CANCELED'
              ? $gettext('Canceled')
              : $gettext('Success')
          }}
          <br />
        </span>
        <span v-if="formattedActivity.donation">
          {{ `$${formattedActivity.donation.amount}` }}
        </span>
        <br />
        <span v-if="formattedActivity.donation">
          {{ $gettext(formattedActivity.donation.frequencyDisplay) }}
        </span>
        <br />
        <span>{{ this.activity.datetimeCreated | formatDateShort($language.current) }}</span>
      </div>
    </div>
    <md-icon
      v-if="formattedActivity.request && formattedActivity.request.hasUnreadMessages"
      class="unread-messages-icon"
    >
      warning
    </md-icon>
    <font-awesome-icon
      class="arrow"
      :icon="['fa', 'chevron-right']"
      :style="{ fontSize: '25px' }"
      v-if="!selected"
    />
    <div v-if="selected" :style="{ width: '30px', alignSelf: 'center' }">
      <!-- class small-load-spinner is used to adjust the size of the loadspinner to fit the ActivityCard  -->
      <LoadSpinner class="small-load-spinner" />
    </div>
  </div>
</template>

<script>
import LoadSpinner from '@/components/LoadSpinner'
import SurveyResponse from '@/services/surveyresponses'
import { mapState } from 'vuex'
import Request from '@/services/requests'
export default {
  name: 'ActivityCard',
  components: {
    LoadSpinner,
  },
  data() {
    return {
      selected: false,
    }
  },
  props: {
    activity: {
      type: Object,
      required: true,
    },
  },
  methods: {
    async onClick(action) {
      this.selected = true
      request: if (action.request) {
        const request = await Request.api.retrieve(action.request.id)
        this.$store.dispatch('updateCurrentRequest', request)

        if (request.status === Request.options.STARTED && this.maxedGrantLimit) {
          this.$router.push({ name: 'Request' })
          break request
        }

        if (request.status === Request.options.STARTED) {
          this.$router.push({ name: await this.nextRoute('PayPalVerify') })
          break request
        }

        this.$router.push({ name: 'RequestSummary' })
      } else if (action.donation) {
        this.$router.push({
          name: 'DonationSummary',
          params: { id: action.donation.id },
        })
      }
      this.selected = false
    },
    async nextRoute(noSurveysRoute) {
      const householdDetailsSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, 'Household Details')
      const finHealthSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, 'Financial Health')

      // if both surveys are complete
      if (householdDetailsSurveyComplete && finHealthSurveyComplete) {
        return noSurveysRoute
      }

      // if both surveys are disabled
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        !this.organizationDetails.isFinancialHealthSurveyEnabled
      ) {
        return noSurveysRoute
      }

      // if house hold details survey is enabled and complete
      if (
        !this.organizationDetails.isFinancialHealthSurveyEnabled &&
        this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        householdDetailsSurveyComplete
      ) {
        return noSurveysRoute
      }

      // if finhealth survey is enabled and complete
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        this.organizationDetails.isFinancialHealthSurveyEnabled &&
        finHealthSurveyComplete
      ) {
        return noSurveysRoute
      }

      if (this.organizationDetails.isHouseholdDetailsSurveyEnabled && !householdDetailsSurveyComplete) {
        return 'HouseholdDetailsRequest'
      }

      // if both surveys are disabled
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        this.organizationDetails.isFinancialHealthSurveyEnabled &&
        !finHealthSurveyComplete
      ) {
        return noSurveysRoute
      }

      // if house hold details survey is enabled and complete
      if (
        !this.organizationDetails.isFinancialHealthSurveyEnabled &&
        this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        householdDetailsSurveyComplete
      ) {
        return noSurveysRoute
      }

      return 'HouseholdDetailsRequest'
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    formattedActivity() {
      let icon
      if (this.activity.actionType === 'Request') {
        icon = require('@/assets/icons/Request.png')
      } else {
        icon = require('@/assets/icons/donate.png')
      }
      return Object.assign(this.activity, { icon: icon })
    },
    maxedGrantLimit() {
      if (!this.organizationDetails.grantLimit) {
        return false
      }
      if (
        this.organizationDetails.grantLimitDuration == 'Lifetime' &&
        this.user.totalPayouts == this.organizationDetails.grantLimit
      ) {
        return true
      }
      if (this.user.totalPayoutsSinceLastGrantLimitReset == this.organizationDetails.grantLimit) {
        return true
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

h4 {
  margin: 0 0 0.5rem 0;
}
p {
  font-family: 'Poppins-Medium';
}
.unread-messages-icon {
  position: absolute;
  left: 1.4rem;
  margin-top: -2.5rem;
}
.activity-card {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 2rem;
  border-radius: 9px;
  margin: 1rem 0;
  box-sizing: border-box;
  background-color: var(--light-brand-color);
  box-shadow: 0 4px 8px 0 $mid-gray;
  cursor: pointer;
}
.image-container {
  width: 6rem;
  padding-right: 1rem;
}
.card-content {
  display: flex;
}
.arrow {
  width: 1rem;
  align-self: center;
}
.small-load-spinner {
  width: 40px;
  display: flex;
  height: 40px;
}
</style>
